import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { missionService } from '../services/missionService';
import { Mission } from '../types/mission';

const statusLabel: Record<string, string> = {
  DRAFT: 'Rascunho',
  READY_FOR_DIAGNOSIS: 'Pronta para diagnostico',
  DIAGNOSING: 'Em diagnostico',
  AWAITING_CONTEXT: 'Aguardando contexto',
  AWAITING_VALIDATION: 'Aguardando validacao',
  PLANNING: 'Em planejamento',
  READY_FOR_EXECUTION: 'Pronta para execucao',
  AWAITING_APPROVAL: 'Aguardando aprovacao',
  EXECUTING: 'Executando',
  MONITORING: 'Monitorando',
  ADJUSTING: 'Em ajuste',
  PAUSED: 'Pausada',
  COMPLETED: 'Concluida',
  CANCELLED: 'Cancelada',
  FAILED: 'Falhou',
};

const statusTone: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-700',
  READY_FOR_DIAGNOSIS: 'bg-blue-100 text-blue-700',
  DIAGNOSING: 'bg-indigo-100 text-indigo-700',
  AWAITING_CONTEXT: 'bg-amber-100 text-amber-700',
  AWAITING_VALIDATION: 'bg-amber-100 text-amber-700',
  PLANNING: 'bg-purple-100 text-purple-700',
  READY_FOR_EXECUTION: 'bg-emerald-100 text-emerald-700',
  AWAITING_APPROVAL: 'bg-orange-100 text-orange-700',
  EXECUTING: 'bg-cyan-100 text-cyan-700',
  MONITORING: 'bg-sky-100 text-sky-700',
  ADJUSTING: 'bg-fuchsia-100 text-fuchsia-700',
  PAUSED: 'bg-slate-200 text-slate-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700',
  CANCELLED: 'bg-rose-100 text-rose-700',
  FAILED: 'bg-red-100 text-red-700',
};

export function MissionsPage() {
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await missionService.listMissions();
      setMissions(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar missoes';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMissions();
  }, []);

  const summary = useMemo(() => ({
    total: missions.length,
    active: missions.filter((mission) => !['COMPLETED', 'CANCELLED', 'FAILED'].includes(mission.status)).length,
    pendingApproval: missions.filter((mission) => mission.status === 'AWAITING_APPROVAL').length,
  }), [missions]);

  return (
    <>
      <Header
        title="Missoes"
        subtitle={`${summary.total} missoes no workspace`}
        actions={<Link to="/missions/new" className="btn-primary">+ Nova Missao</Link>}
      />

      <div className="p-6 space-y-6">
        <section className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm text-slate-500">Missoes ativas</p>
            <p className="mt-2 text-3xl font-semibold text-slate-950">{summary.active}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm text-slate-500">Aguardando aprovacao</p>
            <p className="mt-2 text-3xl font-semibold text-orange-600">{summary.pendingApproval}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm text-slate-500">Total de missoes</p>
            <p className="mt-2 text-3xl font-semibold text-slate-950">{summary.total}</p>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-slate-950">Portifolio de missoes</h2>
            <p className="mt-1 text-sm text-slate-500">
              Abra o centro de comando de cada missao para acompanhar progresso, risco e aprovacoes.
            </p>
          </div>

          {loading ? (
            <div className="px-6 py-10 text-sm text-slate-500">Carregando missoes...</div>
          ) : error ? (
            <div className="px-6 py-10">
              <p className="text-sm text-red-600">{error}</p>
              <button onClick={loadMissions} className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white">
                Tentar novamente
              </button>
            </div>
          ) : missions.length === 0 ? (
            <div className="px-6 py-10">
              <p className="text-sm text-slate-500">Nenhuma missao criada ainda.</p>
              <Link to="/missions/new" className="mt-4 inline-flex rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white">
                Criar primeira missao
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {missions.map((mission) => (
                <div key={mission.id} className="flex flex-col gap-4 px-6 py-5 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-semibold text-slate-950">{mission.title}</h3>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone[mission.status] || statusTone.DRAFT}`}>
                        {statusLabel[mission.status] || mission.status}
                      </span>
                    </div>
                    <p className="mt-2 line-clamp-2 text-sm text-slate-600">{mission.goal}</p>
                    <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-500">
                      <span>
                        Prazo: <strong className="text-slate-700">{mission.deadline ? new Date(mission.deadline).toLocaleDateString('pt-BR') : 'Nao informado'}</strong>
                      </span>
                      <span>
                        Chance de sucesso: <strong className="text-slate-700">{mission.successProbability ?? 0}%</strong>
                      </span>
                      <span>
                        Confianca: <strong className="text-slate-700">{mission.confidenceLevel}</strong>
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Link
                      to={`/missions/${mission.id}/command-center`}
                      className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800"
                    >
                      Abrir centro de comando
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}
